import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  DeleteIcon,
  DownArrowIcon,
  DuplicateIcon,
  ImportIcon,
  LinkIcon,
  MoreVertIcon,
  MoveIcon,
  PlayCircle,
  SidebarCloseIcon,
  StopCircle,
} from '@sketchnote/icons';
import {
  Avatar,
  FileItem,
  FileUpload,
  InviteUsers,
  InviteUsersProps,
  LoaderXs,
  MoreActionMenu,
} from '@sketchnote/shared-components';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API, endpoints } from 'src/api';
import { useBoardStore } from 'src/features/boards';
import { useAgileStore } from 'src/features/scrum/store';

import { useTasksStore } from '../store/useTasksStore';
import { TasksStore, TaskT } from '../types/tasks';
import { ItemAddComment } from './AddComment';
import { AgileFields } from './AgileFields';
import { EditItemAssignDate } from './AssignDate';
import { EditItemAssignee } from './Assignee';
import { EditItemAssigner } from './Assigner';
import { EditItemComments } from './Comments';
import { EditItemDescription } from './Description';
import { EpicTasks } from './EpicTasks';
import { EditEstimatedTime } from './EstimatedTime';
import { EditItemHeader, EditItemHeaderTitle } from './Header';
import { EditItemPriority } from './Priority';
import { SubTasks } from './SubTasks';
import { TaskFields } from './TaskFields';
import { TaskHistory } from './TaskHistory';
const { memo, useCallback, useEffect, useState, useMemo } = React;
import { useSnackbar } from '@components/snackbar';
import Tippy from '@tippyjs/react';
import { frontendHostUrl } from 'config';
import { useMembersT } from 'src/api/services/organisation';
import { PLANRULES } from 'src/constants/plans';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getAllOrgMembers } from 'src/utils/getMemberDetails';
import useSWR from 'swr';

import { MoveBoardItem } from '../../kanban/component/Item';
import { Approvals } from './Approvals';
import { TaskTemplateImport } from './ImportTemplate';
import { TaskTemplateSave } from './SaveTaskTemplate';
import { EditItemStatusColumn } from './StatusColumn';
import { TimerTracker, TimeTrackerDetails } from './TimeTrackerDetails';

export const TaskEdit = memo(
  ({
    id,
    onChangeTask,
    onDelete,
    onDuplicate,
    onMove,
    onClose,
    type,
  }: {
    id: string;
    type?: string;
    onChangeTask?: TasksStore['sendTaskDetailsToParent'];
    onDelete?: (taskId: string) => void;
    onDuplicate?: () => void;
    onMove?: () => void;
    onClose?: () => void;
  }) => {
    const item = useTasksStore(useCallback((s) => s.tasks[id], [id]));
    const fetchTask = useTasksStore((s) => s.fetchTask);
    const fetchEpicTask = useTasksStore((s) => s.fetchEpicTask);
    const onRemoveFile = useTasksStore((s) => s.onRemoveFile);
    const onAddFile = useTasksStore((s) => s.onAddFile);
    const [isRouteChanging, setIsRouteChanging] = useState(false);
    const [showMoveFilesDialog, setShowMoveFilesDialog] = useState(false);
    const boardsStore = useBoardStore();
    const { isEpic, currentEpicItems } = useAgileStore();
    const [members, setMembers] = useState<any>([]);

    const [open] = useSnackbar();

    const fetchMembers = async () => {
      const members = await useMembersT(item?.workspaceId);
      setMembers(members.data.data);
    };

    useEffect(() => {
      item?.workspaceId && fetchMembers();
    }, [item?.id]);

    useEffect(() => {
      if (!id) return;
      useTasksStore.setState({ currentTaskId: id });
      if (router?.query?.type === 'agile') {
        fetchEpicTask(id);
      } else {
        fetchTask(id);
      }

      useTasksStore.setState({ sendTaskDetailsToParent: onChangeTask });

      return () => useTasksStore.setState({ currentTaskId: undefined });
    }, [id]);

    const router = useRouter();
    const [attachments, setAttachments] = useState<any[]>([]);
    const [processingAttachments, setProcessingAttachments] = useState<any[]>([]);
    const [selectedMemberOptions, setSelectedMemberOptions] = useState<any[]>([]);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [openTimeTracker, setOpenTimeTracker] = useState(false);
    const [sidebarMode, setSidebarMode] = useState<
      '' | 'comments' | 'attachments' | 'history' | 'share' | 'approvals'
    >('');

    const { isReadOnly } = useTaskAccess(item?.id || '');

    const isFullScreen = useTasksStore((s) => s.isFullScreen);
    const isAgile = router?.query?.type === 'agile';

    const moreMenuItems = useMemo(() => {
      const items = [
        // {
        //   label: (
        //     <div className="flex  text-gray-999 text-sm ">
        //       <ImportIcon wrapperClass="mt-4 text-gray-500" />
        //       &nbsp;&nbsp; Import a template
        //     </div>
        //   ),
        //   value: 'import',
        // },
        // {
        //   label: (
        //     <div className="flex font-regular text-gray-999 text-sm ">
        //       <AddIcon wrapperClass="mt-4 text-gray-500" />
        //       &nbsp;&nbsp; Save as template
        //     </div>
        //   ),
        //   value: 'save',
        // },
        {
          label: (
            <div className="flex font-regular text-gray-999 text-sm ">
              <DuplicateIcon wrapperClass="mt-4 text-gray-500" />
              &nbsp;&nbsp; Duplicate
            </div>
          ),
          value: 'duplicate',
        },
        {
          label: (
            <div className="flex font-regular text-gray-999 text-sm ">
              <DeleteIcon wrapperClass="mt-4 text-gray-500" />
              &nbsp;&nbsp; Delete
            </div>
          ),
          value: 'delete',
        },
      ];
      if (item && !item.parent) {
        items.push({
          label: (
            <div className="flex font-regular text-gray-999 text-sm ">
              <MoveIcon wrapperClass="mt-4 text-gray-500" />
              &nbsp;&nbsp; Move task
            </div>
          ),
          value: 'move',
        });
      }
      return items;
    }, [item]);

    const agileMenuItems = useMemo(() => {
      const items = [
        {
          label: (
            <div className="flex font-regular text-gray-999 text-sm ">
              <DeleteIcon wrapperClass="mt-4 text-gray-500" />
              &nbsp;&nbsp; Delete
            </div>
          ),
          value: 'delete',
        },
      ];
      if (!isFullScreen) {
        //items.push({
        // label: (
        //   <div className="flex font-regular text-gray-999 text-sm ">
        //     <MoveIcon wrapperClass="mt-4 text-gray-500" />
        //     &nbsp;&nbsp; Move task
        //   </div>
        // ),
        // value: 'move',
        //});
      }
      return items;
    }, [isFullScreen]);

    const [saveTemplateModal, setSaveTemplateModal] = useState(false);
    const [importTemplate, setImportTemplate] = useState(false);

    const handleMenuItemClick = (menuItem) => {
      if (menuItem.value === 'delete' && item?.id) {
        onDelete?.(item.id);
      } else if (menuItem.value === 'move') {
        setShowMoveFilesDialog(true);
      } else if (menuItem.value === 'import') {
        setImportTemplate(true);
      } else if (menuItem.value === 'save') {
        setSaveTemplateModal(true);
      } else if (menuItem.value === 'duplicate') {
        onDuplicate?.();
      }
    };

    const onCloseSidebar = () => setSidebarMode('');

    const sidebarContent = () => {
      if (!item) return <></>;
      switch (sidebarMode) {
        case 'comments':
          return (
            <div className="h-full flex flex-col">
              <div className="flex-grow px-16 h-full overflow-auto">
                <EditItemComments members={members} />
              </div>
              {!isReadOnly && (
                <div className="w-full px-8">
                  <ItemAddComment members={members} />
                </div>
              )}
            </div>
          );
        case 'attachments':
          return (
            <div className="w-full flex justify-between mb-18 px-16">
              <div className="w-full">
                {!isReadOnly && (
                  <>
                    {attachments.length < 10 && !isReadOnly ? (
                      <FileUpload
                        className="mt-14 mb-8 text-gray-500"
                        translations={{
                          attachments_drop_label: 'Upload file',
                          attachments_label: 'Upload file ',
                        }}
                        onDrop={handleFileUpload}
                        maxFileSize={3e7}
                        multiple={false}
                      />
                    ) : !isReadOnly ? (
                      <div className="text-sm py-16">Max limit 10 attachments reached</div>
                    ) : (
                      <></>
                    )}
                  </>
                )}

                {processingAttachments?.map((attachment, index: number) => (
                  <FileItem
                    fileName={attachment.name}
                    fileSize={attachment.size}
                    key={`${attachment.size}-${index}`}
                    onRemove={() => handleRemoveFile(attachment)}
                    className="mb-8"
                    isLoading
                    isReadOnly={isReadOnly}
                  />
                ))}
                {attachments?.map((attachment, index: number) => (
                  <FileItem
                    fileName={attachment.name}
                    fileSize={attachment.size}
                    filePath={attachment.url}
                    key={`${attachment.size}-${index}`}
                    onRemove={() => handleRemoveFile(attachment)}
                    isReadOnly={isReadOnly}
                    className="mb-8"
                  />
                ))}
              </div>
            </div>
          );
        case 'history':
          return (
            <div className="px-16">
              <TaskHistory task={item} OnClickBackArrow={handleTaskHistoryOpen} />
            </div>
          );
        case 'share':
          return <ShareTaskSidebar item={item} />;
        case 'approvals':
          return <Approvals item={item} members={members} />;
        default:
          return <></>;
      }
    };

    const sidebarHeadings = {
      comments: {
        heading: 'Comments',
        subtext: 'View comment threads here',
      },
      attachments: {
        heading: 'Attachments',
        subtext: 'View all attachments here',
      },
      history: {
        heading: 'History',
        subtext: 'View this task’s history here',
      },
      share: {
        heading: 'Share',
        subtext: 'Share this task with your team here',
      },
      approvals: {
        heading: 'Approval Configuration',
        subtext: "Get clear and concise trail of the task's approval journey",
      },
    };

    const isSidebarOpen = !!sidebarMode;

    useEffect(() => {
      if (item?.attachments?.length) {
        const files = item.attachments.map((file) => {
          const fileItem: any = new File([], file.name);
          fileItem.url = file.url;
          return fileItem;
        });
        setAttachments(files);
      } else setAttachments([]);
      setProcessingAttachments([]);
      GetSelectedMemberOptions();
    }, [item]);

    useEffect(() => {
      setIsRouteChanging(true);
      setTimeout(() => {
        setIsRouteChanging(false);
      }, 100);
    }, [router]);

    if (!item) return <></>;

    function GetSelectedMemberOptions() {
      item?.fields?.map((customField) => {
        if (customField.type === 'person') {
          Array.isArray(customField.data) &&
            customField.data?.map((member) => {
              setSelectedMemberOptions((prev) => {
                if (!prev) return [];
                const item = prev.find((prevItem) => prevItem?.memberId === member.memberId);
                if (!item) {
                  prev.push(member);
                }
                return [...prev];
              });
            });
        }
      });
    }

    const getRouteBasedOnRouteBack = (routeBack, mode, orgId) => {
      switch (routeBack) {
        case 'default':
          return `/${mode}/${orgId}/files/${item.fileId}`;
        case 'mytaskroute':
          return `/${mode}/${orgId}/mytasks`;
        case 'alltasksroute':
          return `/${mode}/${orgId}/alltasks`;
        default:
          return `/${mode}/${orgId}/files/${item.fileId}?view=${item.board?.id}&viewId=${
            router.query.viewId
          }&type=${router.query.routeBack || router.query.type}`;
      }
    };

    const handleTaskViewClose = () => {
      if (onClose) {
        onClose();
        return;
      }
      const orgId = router?.query?.orgId;
      const mode = router?.query?.mode;
      const fileId = router?.query?.slug;
      let link = '';
      if (type === 'scrum') {
        link = `/${mode}/${orgId}/files/${fileId}?view=${fileId}&viewId=${router.query.viewId}&type=${router.query.type}`;
      } else if (boardsStore.isPublicView) {
        //public
        link = `/${mode}/${orgId}/files/${item.fileId}/public?view=${item.board?.id}&viewId=${router.query.viewId}&type=${router.query.type}`;
      } else {
        link = getRouteBasedOnRouteBack(router.query.routeBack, mode, orgId);
      }
      router.push(link);
    };

    const handleFileUpload = (files) => {
      files.forEach(async (file) => {
        const data = new FormData();

        data.append('file', file);
        setProcessingAttachments((prevAttaching: File[]) => [...prevAttaching, file]);

        let success = false;

        const response = await API.post(endpoints.creationFile.url, data, {
          headers: { ['Content-Type']: 'multipart/form-data' },
        });

        if (response.data?.data?.message) {
          const payload = {
            itemId: item.id!,
            fileName: file.path,
            filePath: response.data?.data?.message.url,
          };
          success = await onAddFile(payload);
        }
        if (!success) {
          open({
            text: 'Failed to upload file',
            snackType: 'error',
            duration: 3000,
            position: 'top-center',
          });
        }
      });
    };

    const handleRemoveFile = async (attachment) => {
      if (!isReadOnly) {
        const payload = {
          itemId: item.id!,
          filePath: attachment.url,
        };
        const filteredAttachements = attachments.filter((i) => i !== attachment);
        setAttachments(filteredAttachements);
        setProcessingAttachments((prevAttaching: File[]) => [...prevAttaching, attachment]);
        const success = await onRemoveFile(payload);
        if (!success) {
          open({
            text: 'Failed to remove file',
            snackType: 'error',
            duration: 3000,
            position: 'top-center',
          });
        }
      }
    };

    const handleTaskHistoryOpen = async () => {
      if (isHistoryOpen) {
        setIsHistoryOpen(false);
      } else {
        setIsHistoryOpen(true);
      }
    };

    const handleSidebarModeChange = (mode: typeof sidebarMode) => {
      if (mode === sidebarMode) {
        onCloseSidebar();
      } else {
        setSidebarMode(mode);
      }
    };

    if (isRouteChanging || !item.id) {
      if (!item.isLoading && !item.id)
        return <div className="flex justify-center items-center h-full">Task not found</div>;
      return (
        <div className="flex flex-grow h-full items-center justify-center relative">
          <LoaderXs className="text-xss" />
        </div>
      );
    }

    const handleMoveComplete = () => {
      if (isFullScreen) {
        // fetch file
        if (router?.query?.type === 'agile') {
          fetchEpicTask(id);
        } else {
          fetchTask(id);
        }
      } else {
        // close task dialog
        handleTaskViewClose();
      }
    };

    return (
      <>
        <EditItemHeader
          onTaskViewClose={handleTaskViewClose}
          task={item}
          onDeleteTask={onDelete}
          onDuplicateTask={onDuplicate}
          onMove={onMove}
          onExploreClick={handleTaskHistoryOpen}
          onCommentsClick={() => handleSidebarModeChange('comments')}
          onAttachmentsClick={() => handleSidebarModeChange('attachments')}
          onHistoryClick={() => handleSidebarModeChange('history')}
          onShareClick={() => handleSidebarModeChange('share')}
          onApprovalsClick={() => handleSidebarModeChange('approvals')}
          sidebarMode={sidebarMode}
        />
        <div className="relative flex flex-col flex-grow overflow-y-scroll overflow-x-hidden">
          <div className="flex items-center gap-24 px-32 py-8 border-b border-gray-100">
            <div className="flex items-center gap-24 flex-grow">
              <div>
                <EditItemStatusColumn />
              </div>
              <div>
                <EditItemPriority />
              </div>
            </div>
            <div className="flex gap-12 justify-self-end">
              <div>
                <div className="text-gray-500 text-sm text-center">Assignee</div>
                <span className="flex items-center justify-center mt-4">
                  <EditItemAssignee members={members} />
                </span>
              </div>
              <div className="border-r h-16 w-px mt-16" />
              <div>
                <div className="text-gray-500 text-sm text-center">Assigner</div>
                <span className="flex items-center justify-center mt-4">
                  <EditItemAssigner members={members} />
                </span>
              </div>
              <div className="border-r h-16 w-px mt-16" />
              <div>
                <div className="text-gray-500 text-sm text-center">Due date</div>
                <EditItemAssignDate />
              </div>
              <div className="border-r h-16 w-px mt-16" />
              <div>
                <div className="text-gray-500 text-sm text-center">Estimated time</div>
                <EditEstimatedTime members={selectedMemberOptions} />
              </div>
              <div className="border-r h-16 w-px mt-16" />
              <div>
                <div className="text-gray-500 text-sm text-center">Time tracked</div>
                <Popover isOpen={openTimeTracker} onClose={() => setOpenTimeTracker(false)}>
                  <PopoverTrigger>
                    <button
                      className="flex gap-2 items-center text-gray-500"
                      onClick={() => setOpenTimeTracker(!openTimeTracker)}
                    >
                      {item?.timer?.isRunning ? <StopCircle size={14} /> : <PlayCircle size={14} />}

                      <TimerTracker
                        className=" text-gray-500 w-93 text-sm"
                        startTime={item?.timer?.startTime}
                        stopTime={item?.timer?.stopTime}
                        isRunning={item?.timer?.isRunning}
                        timeTrackedTaskId={item.id}
                      />

                      <ChevronDownIcon size={15} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <TimeTrackerDetails
                      taskId={item?.id}
                      taskTitle={item?.name}
                      isRunning={item?.timer?.isRunning}
                      startTime={item?.timer?.startTime}
                      stopTime={item?.timer?.stopTime}
                      setOpenTimeTracker={setOpenTimeTracker}
                      timeTrackedTaskId={item.id}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {!isReadOnly && (
                <div>
                  <MoreActionMenu
                    classNames={{ labelWrap: 'mt-16' }}
                    menuItems={isAgile ? agileMenuItems : moreMenuItems}
                    icon={<MoreVertIcon className="text-gray-500" />}
                    onClickMenuItem={handleMenuItemClick}
                  />
                </div>
              )}
            </div>
          </div>
          <div
            className={classNames(
              'flex w-full flex-grow',
              isSidebarOpen ? 'no-scrollbar overflow-hidden pr-6 pb-4' : 'overflow-y-scroll'
            )}
          >
            <div
              className={classNames(
                'flex-grow px-32 pb-4 no-scrollbar w-full',
                isSidebarOpen ? 'no-scrollbar overflow-hidden' : 'overflow-auto'
              )}
            >
              <EditItemHeaderTitle task={item} />
              {router?.query?.type === 'agile' && (
                <section className="border-b mb-24">
                  <div className="mb-24">
                    <AgileFields task={item} />
                  </div>
                </section>
              )}
              {isEpic && (
                <>
                  <div className="flex flex-row mb-24 justify-between">
                    <section className=" flex">
                      <div className="flex flex-col">
                        <h5 className="mb-6 text-gray-800 font-medium text-md">
                          📇️ Linked items ({currentEpicItems?.length})
                        </h5>
                        <p className="text-sm font-regular text-gray-600">
                          These are tasks, bugs and user stories linked to this epic{' '}
                        </p>
                      </div>
                    </section>
                  </div>
                  <section className=" border-b mb-40">
                    <EpicTasks task={item} items={currentEpicItems} />
                  </section>
                </>
              )}
              <section className="mb-32">
                <TaskFields task={item} members={members} />
              </section>

              {!item.isSubtask && (
                <section className="mb-40">
                  <SubTasks task={item} />
                </section>
              )}

              <section className="mb-40">
                <EditItemDescription task={item} />
              </section>
            </div>
            {importTemplate && (
              <TaskTemplateImport
                task={item}
                importTemplate={importTemplate}
                setImportTemplate={setImportTemplate}
              />
            )}
            {saveTemplateModal && (
              <TaskTemplateSave
                task={item}
                saveTemplateModal={saveTemplateModal}
                setSaveTemplateModal={setSaveTemplateModal}
              />
            )}

            <div
              className="absolute inset-0 z-10 "
              style={{ visibility: isSidebarOpen ? 'visible' : 'hidden' }}
            >
              <div className="w-full h-full flex justify-end">
                <div className=" absolute z-0 inset-0 cursor-pointer" onClick={onCloseSidebar} />
                <div
                  style={{
                    transition: 'transform 0.3s ease-out',
                    transform: isSidebarOpen ? 'translateX(0)' : 'translateX(100%)',
                    visibility: isSidebarOpen ? 'visible' : 'hidden',
                    boxShadow: ' -4px 0px 16px 0px rgba(0, 0, 0, 0.10)',
                  }}
                  className=" transition-width duration-300 xxxs:w-400 bg-white flex flex-col relative h-full z-10 border-l"
                >
                  <div className="w-full bg-gray-100 px-24 py-8 relative">
                    <div className="text-gray-900 text-sm font-medium">
                      {sidebarMode && sidebarHeadings[sidebarMode].heading}
                    </div>
                    <span className="text-gray-600 text-xs">
                      {sidebarMode && sidebarHeadings[sidebarMode].subtext}
                    </span>
                    <span
                      className="absolute inset-y-0 flex items-center h-full cursor-pointer"
                      style={{ left: -14 }}
                      onClick={onCloseSidebar}
                    >
                      <SidebarCloseIcon size={28} />
                    </span>
                  </div>
                  <div className="flex-grow  h-full overflow-auto bg-white">{sidebarContent()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Modal
          isOpen={showMoveFilesDialog}
          onClose={() => setShowMoveFilesDialog(false)}
          motionPreset="none"
          isCentered
        >
          <ModalOverlay />
          <ModalContent minW={650} width={650} className="max-w-650 bg-white rounded-lg">
            <MoveBoardItem
              selectedItems={[item.id]}
              onCancel={() => setShowMoveFilesDialog(false)}
              onAfterMove={handleMoveComplete}
            />
          </ModalContent>
        </Modal>
      </>
    );
  }
);

const ShareTaskSidebar = ({ item: task }: { item: TaskT }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const router = useRouter();
  const [open] = useSnackbar();
  const orgId = router?.query?.orgId;
  const sharedWith = useTasksStore((s) => s.tasks[task?.id || '']?.sharedWith || []);

  const orgMembers = useMemo(() => getAllOrgMembers(), []);
  const authData = useAuth((s) => s.userDetails);
  const plan = authData?.plan?.type || '';

  const onShareTask = useTasksStore((s) => s.onShareTask);
  const onChangeTaskShareAccess = useTasksStore((s) => s.onChangeTaskShareAccess);
  const onRemoveTaskShareAccess = useTasksStore((s) => s.onRemoveTaskShareAccess);
  const { data: guestList } = useSWR('/user/guests', API.get);

  const { isReadOnly, isFullAccess } = useTaskAccess(task.id || '');

  const handleInviteUsers: InviteUsersProps['onSubmit'] = async (data) => {
    setIsShareOpen(false);
    if (task?.id) {
      onShareTask({
        access: data.access.value,
        itemId: task.id,
        members: data.items,
      });
    }
  };
  const handleOnChangeAccess = (member) => async (access) => {
    if (access.value === 'remove') return handleRemoveShareAccess(member);
    if (task?.id) {
      const success = await onChangeTaskShareAccess({
        access: access.value,
        itemId: task.id,
        member: member,
      });
      if (!success) {
        open({
          text: 'Failed to update access',
          snackType: 'error',
          duration: 3000,
          position: 'top-center',
        });
      }
    }
  };

  const handleRemoveShareAccess = async (member) => {
    if (task?.id) {
      const success = await onRemoveTaskShareAccess({
        itemId: task.id,
        member: member,
      });
      if (!success) {
        open({
          text: 'Failed to remove access',
          snackType: 'error',
          duration: 3000,
          position: 'top-center',
        });
      }
    }
  };

  return (
    <div className="bg-white">
      {isFullAccess && (
        <div className="flex w-full px-12 py-6 bg-gray-100 rounded-md">
          <input
            className="w-full bg-gray-100 placeholder-gray-600 text-gray-600 text-sm"
            placeholder="Add a name or email"
            onFocus={(e) => {
              setIsShareOpen(true);
              e.target.blur();
            }}
          />
        </div>
      )}

      <div className="text-xs text-gray-600 flex px-20">
        {task?.id && !isReadOnly && (
          <Tippy
            content="This link will only work for you and other invited users "
            className="w-221 h-auto bg-gray-999 tippy text-sm text-center"
            placement="top"
          >
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${frontendHostUrl}w/${orgId}/tasks/${task.id}`);
                setIsCopied(true);
                setTimeout(() => {
                  setIsCopied(false);
                }, 1000);
              }}
              className="text-primary-500 cursor-pointer font-medium flex gap-4 items-center mt-6"
            >
              <span className="text-blue-500 font-medium">
                <LinkIcon />
              </span>
              {isCopied ? 'Copied!' : 'Copy link'}
            </button>
          </Tippy>
        )}
      </div>
      {sharedWith.map((item) => {
        const actionMenuOptions = [
          { label: 'Full access', value: 'full' },
          // { label: 'Edit access', value: 'edit' },
          { label: 'View access', value: 'view' },
          // { label: 'Comment access', value: 'comment' },
          { label: 'Remove access', value: 'remove' },
        ];
        const access = actionMenuOptions.find((obj) => obj.value === item.access);

        return (
          <div className="flex items-center justify-between px-20 py-16" key={item.member.memberId}>
            <div className="flex items-center w-full">
              <Avatar name={item.member.firstname} type="photo" src={item.member.avatar} />
              <div className="ml-12">
                <p className="text-sm font-medium">{item.member.firstname}</p>
                <p className="text-xs">
                  <span className="">{item.member.email}</span>
                </p>
              </div>
            </div>
            {isFullAccess && (
              <MoreActionMenu
                icon={
                  <span className="flex items-center text-gray-600 text-sm">
                    {access?.label || item.access} <DownArrowIcon wrapperClass="ml-8" />
                  </span>
                }
                onClickMenuItem={handleOnChangeAccess(item)}
                menuItems={actionMenuOptions}
              />
            )}
          </div>
        );
      })}

      <Modal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)}>
        <ModalOverlay />
        <ModalContent
          maxWidth="50rem"
          overflow="auto"
          borderRadius="0.8rem"
          className="bg-white static rounded-lg"
          margin="auto"
        >
          <ModalBody className="flex" p="0">
            <div className="w-full">
              <InviteUsers
                members={
                  orgMembers
                    ?.filter((obj) => obj.memberId !== authData?.memberInformation?.memberId)
                    .filter(
                      (obj) =>
                        !sharedWith?.find(
                          (sharedMember) => sharedMember.member.memberId === obj.memberId
                        )
                    )
                    .map((obj) => ({
                      firstname: obj.firstname,
                      email: obj.email,
                      type: obj.role,
                      avatar: obj.avatar,
                      memberId: obj.memberId,
                    })) || []
                }
                accessLevels={[
                  { label: 'Full access', value: 'full' },
                  // { label: 'Edit access', value: 'edit' },
                  { label: 'View access', value: 'view' },
                  // { label: 'Comment access', value: 'comment' },
                ]}
                onSubmit={handleInviteUsers}
                userInviteLimit={PLANRULES.guests[plan]}
                guestCount={guestList?.data?.data?.length}
                isFreeTrial={!!authData?.plan?.trial}
              />
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

TaskEdit.displayName = 'Task';
